import { useCallback, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, ClipboardEvent, DragEvent } from 'react';
import { sendFeedback } from '@sentry/react';
import { Bug, ImagePlus, Lightbulb, MessageSquarePlus, X } from 'lucide-react';
import { Button, Callout, Dialog, Input } from '../primitives';
import { isSentryEnabled } from '../../sentry';
import { useAuth } from '../../hooks/useAuth';
import { useFeatureEnabled } from '../../hooks/useTenantConfig';
import {
  fileFeedbackReport,
  reportAsText,
  type FeedbackKind,
  type FeedbackReportFields,
} from '../../services/feedbackApi';
import './Feedback.css';

type SubmitState = 'idle' | 'submitting' | 'sent' | 'error';

export const MAX_ATTACHMENTS = 5;
export const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024; // 5MB per image
const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];
const MAX_SUMMARY_CHARS = 150;

const emptyFields = (kind: FeedbackKind): FeedbackReportFields => ({
  kind,
  summary: '',
  details: '',
  steps: '',
  expected: '',
  benefit: '',
});

interface FieldCopy {
  key: 'details' | 'steps' | 'expected' | 'benefit';
  label: string;
  placeholder: string;
  required?: boolean;
  rows: number;
}

const FIELDS: Record<FeedbackKind, FieldCopy[]> = {
  problem: [
    { key: 'details', label: 'What happened', placeholder: 'What you saw, and on which page.', required: true, rows: 4 },
    { key: 'steps', label: 'Steps to reproduce', placeholder: '1. Open …\n2. Click …\n3. …', rows: 4 },
    { key: 'expected', label: 'What you expected', placeholder: 'What should have happened instead.', rows: 2 },
  ],
  idea: [
    { key: 'details', label: "What you'd like", placeholder: 'The change or feature, and where in the app.', required: true, rows: 4 },
    { key: 'benefit', label: 'Why it would help', placeholder: 'What it would let you do, or the time it would save.', rows: 3 },
  ],
};

const KIND_OPTIONS: Array<{ kind: FeedbackKind; title: string; hint: string; icon: typeof Bug }> = [
  { kind: 'problem', title: 'Problem', hint: "Something isn't working", icon: Bug },
  { kind: 'idea', title: 'Idea', hint: "Something you'd like", icon: Lightbulb },
];

const SUMMARY_PLACEHOLDER: Record<FeedbackKind, string> = {
  problem: 'One line, e.g. Hub Reports total shows 0 after changing the window',
  idea: 'One line, e.g. Export the hub pivot to Excel',
};

const EXAMPLES: Record<FeedbackKind, Array<[string, string]>> = {
  problem: [
    ['Summary', 'Hub Reports total shows 0 after changing the connection window'],
    ['What happened', 'I changed the maximum connection time to 240 min. The Total row now shows 0 connections, but the table below still lists them.'],
    ['Steps to reproduce', '1. Open Hub Reports for any sample week\n2. Set maximum connection time to 240\n3. Click Calculate'],
    ['What you expected', 'The Total row to add up the table.'],
  ],
  idea: [
    ['Summary', 'Export the hub pivot to Excel'],
    ["What you'd like", 'A download button on the Hub Reports pivot that saves the current view as an .xlsx file.'],
    ['Why it would help', 'I copy the pivot into Excel every week for our network review. A download would save time and keep the layout.'],
  ],
};

const PASTE_SHORTCUT =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘V' : 'Ctrl+V';

/**
 * "Send feedback" button below the sidebar's Settings section (#641, #1062).
 *
 * Opens a guided report form (problem or idea, one-line summary, the fields a
 * good report needs, a worked example, screenshots by paste, drop or browse).
 * Submitting sends the report to Sentry User Feedback (browser, page, release,
 * session replay, screenshots) and then to the backend, which files it as a
 * GitHub issue linked to the Sentry event. Either path succeeding counts as
 * delivered: an ad blocker can stop Sentry, and a tenant without a GitHub
 * token answers 503 for the issue step.
 *
 * Renders nothing unless BOTH the per-tenant `feedback` feature flag is on
 * (tenants/<tenant>.json; absence fails closed) AND Sentry is configured for
 * this build (`VITE_SENTRY_DSN` set).
 */
export function FeedbackButton({ collapsed }: { collapsed: boolean }) {
  const feedbackEnabled = useFeatureEnabled('feedback');
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [fields, setFields] = useState<FeedbackReportFields>(() => emptyFields('problem'));
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [showExample, setShowExample] = useState(false);
  const [state, setState] = useState<SubmitState>('idle');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reporterName = useMemo(() => {
    if (!user) return '';
    return [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  }, [user]);
  const reporterEmail = user?.email ?? '';

  const openForm = useCallback(() => {
    setFields(emptyFields('problem'));
    setFiles([]);
    setFileError('');
    setShowExample(false);
    setState('idle');
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    // Close before any async work so the dialog never gets stuck.
    setIsOpen(false);
  }, []);

  const setField = useCallback((key: keyof FeedbackReportFields, value: string) => {
    setFields((prev) => ({ ...prev, [key]: value }));
  }, []);

  const addFiles = useCallback((picked: File[]) => {
    if (picked.length === 0) return;
    setFiles((prev) => {
      const errors: string[] = [];
      const next = [...prev];
      for (const file of picked) {
        if (next.length >= MAX_ATTACHMENTS) {
          errors.push(`You can attach up to ${MAX_ATTACHMENTS} images.`);
          break;
        }
        if (!IMAGE_TYPES.includes(file.type)) {
          errors.push(`${file.name} is not a PNG, JPEG, GIF or WebP image.`);
          continue;
        }
        if (file.size > MAX_ATTACHMENT_BYTES) {
          errors.push(`${file.name} is larger than 5MB. Attach a smaller image.`);
          continue;
        }
        next.push(file);
      }
      setFileError(errors[0] ?? '');
      return next;
    });
  }, []);

  const handlePick = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    // Allow re-picking the same file after a remove.
    e.target.value = '';
    addFiles(picked);
  }, [addFiles]);

  const handlePaste = useCallback((e: ClipboardEvent<HTMLDivElement>) => {
    const images = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'));
    if (images.length === 0) return; // plain text paste goes to the focused field
    e.preventDefault();
    const stamp = new Date().toISOString().slice(11, 19).replace(/:/g, '');
    addFiles(images.map((f, i) => new File([f], `screenshot-${stamp}-${i + 1}.${f.type.split('/')[1] || 'png'}`, { type: f.type })));
  }, [addFiles]);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (!Array.from(e.dataTransfer?.types ?? []).includes('Files')) return;
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);
    addFiles(Array.from(e.dataTransfer?.files ?? []));
  }, [addFiles]);

  const handleRemoveFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setFileError('');
  }, []);

  const canSubmit = fields.summary.trim().length > 0 && fields.details.trim().length > 0 && state !== 'submitting';

  const handleSubmit = useCallback(async () => {
    setState('submitting');
    let sentryEventId: string | null = null;
    try {
      const attachments = await Promise.all(
        files.map(async (file) => ({
          filename: file.name,
          data: new Uint8Array(await file.arrayBuffer()),
          contentType: file.type || 'application/octet-stream',
        })),
      );
      sentryEventId = await sendFeedback(
        {
          message: reportAsText(fields),
          name: reporterName || undefined,
          email: reporterEmail || undefined,
          url: window.location.href,
          source: 'feedback-button',
          tags: { feedback_kind: fields.kind },
        },
        {
          includeReplay: true,
          ...(attachments.length > 0 ? { attachments } : {}),
        },
      );
    } catch {
      sentryEventId = null;
    }
    let filed = false;
    try {
      filed = (await fileFeedbackReport(fields, files, sentryEventId)) === 'filed';
    } catch {
      filed = false;
    }
    setState(sentryEventId || filed ? 'sent' : 'error');
  }, [fields, files, reporterName, reporterEmail]);

  if (!feedbackEnabled || !isSentryEnabled()) {
    return null;
  }

  const kind = fields.kind;

  return (
    <div className="sidebar-feedback">
      <button
        type="button"
        className="feedback-pin"
        onClick={openForm}
        title={collapsed ? 'Send feedback' : undefined}
        aria-haspopup="dialog"
      >
        <span className="feedback-pin__icon">
          <MessageSquarePlus size={16} strokeWidth={1.6} aria-hidden />
        </span>
        <span className="feedback-pin__label">Send feedback</span>
      </button>

      <Dialog isOpen={isOpen} onClose={handleClose} size="md" title="Send feedback">
        {state === 'sent' ? (
          <div className="lm-form">
            <Callout intent="success" title="Feedback sent">
              Thanks. The team reads every report and will get back to you if they need more detail.
            </Callout>
            <div className="lm-feedback-actions">
              <Button variant="accent" onClick={handleClose}>Close</Button>
            </div>
          </div>
        ) : (
          <div
            className="lm-form lm-feedback-form"
            onPaste={handlePaste}
            onDragOver={handleDragOver}
            onDragLeave={() => setDragActive(false)}
            onDrop={handleDrop}
          >
            <div className="lm-feedback-kind" role="radiogroup" aria-label="Type of feedback">
              {KIND_OPTIONS.map(({ kind: option, title, hint, icon: Icon }) => (
                <button
                  key={option}
                  type="button"
                  role="radio"
                  aria-checked={kind === option}
                  className={`lm-feedback-kind__opt${kind === option ? ' is-on' : ''}`}
                  onClick={() => setField('kind', option)}
                >
                  <span className="lm-feedback-kind__title">
                    <Icon size={14} strokeWidth={1.8} aria-hidden />
                    {title}
                  </span>
                  <span className="lm-feedback-kind__hint">{hint}</span>
                </button>
              ))}
            </div>

            <div className="lm-feedback-example">
              <button
                type="button"
                className="lm-feedback-link"
                aria-expanded={showExample}
                onClick={() => setShowExample((v) => !v)}
              >
                {showExample ? 'Hide the example' : 'See an example of a good report'}
              </button>
              {showExample && (
                <dl className="lm-feedback-example__body">
                  {EXAMPLES[kind].map(([term, text]) => (
                    <div key={term} className="lm-feedback-example__row">
                      <dt>{term}</dt>
                      <dd>{text}</dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>

            {state === 'error' && (
              <Callout intent="danger" title="Feedback not sent">
                Your feedback did not reach the team. Check your connection and try again.
              </Callout>
            )}

            <label className="lm-form__field">
              <span className="lm-form__label">Summary</span>
              <Input
                value={fields.summary}
                onChange={(e) => setField('summary', e.target.value)}
                placeholder={SUMMARY_PLACEHOLDER[kind]}
                maxLength={MAX_SUMMARY_CHARS}
                fullWidth
                autoFocus
              />
            </label>

            {FIELDS[kind].map(({ key, label, placeholder, required, rows }) => (
              <label key={`${kind}-${key}`} className="lm-form__field">
                <span className="lm-form__label">
                  {label}
                  {!required && <span className="lm-form__optional"> (optional)</span>}
                </span>
                <textarea
                  className="lm-feedback-textarea"
                  value={fields[key]}
                  onChange={(e) => setField(key, e.target.value)}
                  placeholder={placeholder}
                  rows={rows}
                />
              </label>
            ))}

            <div className="lm-form__field">
              <span className="lm-form__label">
                Screenshots <span className="lm-form__optional">(optional, up to {MAX_ATTACHMENTS})</span>
              </span>
              {fileError && <Callout intent="danger">{fileError}</Callout>}
              {files.length > 0 && (
                <ul className="lm-feedback-attachments">
                  {files.map((file, i) => (
                    <li key={`${file.name}-${i}`} className="lm-feedback-attachment">
                      <FilePreview file={file} />
                      <span className="lm-feedback-attachment__name" title={file.name}>
                        {file.name}
                      </span>
                      <button
                        type="button"
                        className="lm-feedback-attachment__remove"
                        onClick={() => handleRemoveFile(i)}
                        aria-label={`Remove ${file.name}`}
                      >
                        <X size={12} strokeWidth={2} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {files.length < MAX_ATTACHMENTS && (
                <div className={`lm-feedback-drop${dragActive ? ' is-active' : ''}`}>
                  <ImagePlus size={16} strokeWidth={1.6} aria-hidden />
                  <span>
                    Paste a screenshot ({PASTE_SHORTCUT}), drop images here, or{' '}
                    <button
                      type="button"
                      className="lm-feedback-link"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      browse
                    </button>
                  </span>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={IMAGE_TYPES.join(',')}
                multiple
                onChange={handlePick}
                className="lm-feedback-file-input"
                aria-label="Attach images"
                tabIndex={-1}
              />
            </div>

            <p className="lm-feedback-identity">
              {reporterName || reporterEmail
                ? <>Sending as {reporterName || reporterEmail}{reporterName && reporterEmail ? ` (${reporterEmail})` : ''}. </>
                : null}
              The page you are on, your browser and this session are attached automatically.
            </p>

            <div className="lm-feedback-actions">
              <Button variant="ghost" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                variant="accent"
                onClick={handleSubmit}
                disabled={!canSubmit}
                loading={state === 'submitting'}
              >
                Send feedback
              </Button>
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}

/** Tiny thumbnail for a picked image; falls back to nothing where object URLs
 * are unavailable (jsdom). */
function FilePreview({ file }: { file: File }) {
  const url = useMemo(
    () => (typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : ''),
    [file],
  );
  if (!url) return null;
  return (
    <img
      className="lm-feedback-attachment__thumb"
      src={url}
      alt=""
      onLoad={() => URL.revokeObjectURL(url)}
    />
  );
}

export default FeedbackButton;
